import { AlertTriangle, Lock } from "lucide-react";
import { useMemo, useState } from "react";

import { EnvironmentBadge } from "@/components/app-registration-badges";
import {
  ConsentConsequences,
  PermissionDiff,
} from "@/components/app-registration-permission-change";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  type AppRegistrationRequestDetail,
  useAppRegistrationAmendmentBase,
  useAppRegistrationScopes,
  useCreateAppRegistrationAmendment,
} from "@/lib/api";
import { readApiErrorMessage } from "@/lib/api-errors";
import {
  diffScopeKeys,
  IDENTITY_SCOPE_KEYS,
  indexScopes,
  lockedScopeKeysOf,
  withLockedScopes,
  withoutLockedScopes,
} from "@/lib/app-registration-scopes";
import { logger } from "@/lib/logger";

import { ScopePicker } from "./app-registration-scope-picker";

/** Server-side caps, mirrored so the form fails before the round-trip does. */
const MIN_PURPOSE_LENGTH = 10;
const MAX_SCOPES = 25;

interface AppRegistrationAmendDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The registration being changed — any row in its chain will do. */
  request: AppRegistrationRequestDetail;
}

/**
 * Change the delegated permissions on a registration that already exists.
 *
 * Changing permissions used to mean rejecting the request and raising a new
 * one, which deletes and recreates the application — and with it the client id
 * every consumer has been handed. This edits the application in place instead:
 * same registration, same client id, same federated credential.
 *
 * What it cannot do is move consent, and the form says so before the requester
 * submits rather than after. See `ConsentConsequences`.
 */
export function AppRegistrationAmendDialog({
  open,
  onOpenChange,
  request,
}: AppRegistrationAmendDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Change permissions</DialogTitle>
          <DialogDescription>
            For <span className="font-mono">{request.display_name}</span>. An admin reviews the
            change before anything is altered in Entra.
          </DialogDescription>
          {request.environment && (
            <div className="flex items-center gap-2 pt-1">
              <EnvironmentBadge environment={request.environment} />
              <span className="text-muted-foreground text-xs">
                This registration serves that environment only.
              </span>
            </div>
          )}
        </DialogHeader>
        {open && (
          // Remount per opening so the picker always starts from a freshly
          // fetched base — the value it is seeded with is the one the server
          // compares against, so a stale one costs the requester their edit.
          <AmendForm key={request.id} request={request} onClose={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function AmendForm({
  request,
  onClose,
}: {
  request: AppRegistrationRequestDetail;
  onClose: () => void;
}) {
  const base = useAppRegistrationAmendmentBase(request.id);
  // Read out once and narrowed here rather than defaulted at each use. The
  // schema makes it optional because the field has a server-side default;
  // absent on the wire would mean the answer never arrived, and `?? []` would
  // turn that into "this registration has no permissions" -- which is a real
  // state, and the single most destructive thing to assume.
  const baseScopeKeys = base.data?.scope_keys;

  if (base.isLoading) {
    return (
      <DialogBody>
        <p className="text-muted-foreground text-sm">
          Reading the registration&apos;s current permissions…
        </p>
      </DialogBody>
    );
  }

  // Never fall back to an empty base. "This registration has no permissions"
  // is a real state and a destructive thing to assume: seeding the picker with
  // it would turn every submission into a request to strip everything.
  if (base.error || !base.data || baseScopeKeys === undefined) {
    return (
      <>
        <DialogBody>
          <p className="text-destructive flex items-start gap-2 text-sm">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            <span>
              Couldn&apos;t read what this registration currently has, so there is nothing safe to
              edit from. Close the dialog and reopen it to retry.
            </span>
          </p>
        </DialogBody>
        <DialogFooter className="px-6 pb-6">
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </>
    );
  }

  if (!base.data.amendable) {
    return (
      <Blocked reason={base.data.blocked_reason} scopeKeys={baseScopeKeys} onClose={onClose} />
    );
  }

  return (
    <AmendFields
      request={request}
      baseScopeKeys={baseScopeKeys}
      lockedScopeKeys={lockedScopeKeysOf(base.data)}
      // The server's answer first; the row's origin only for a server that
      // predates the flag. The server applies its own rule either way.
      signInRegistration={base.data.sign_in_registration ?? request.origin === "scaffold_sso"}
      onClose={onClose}
    />
  );
}

/** Why the change cannot be raised, in the server's own words. */
function Blocked({
  reason,
  scopeKeys,
  onClose,
}: {
  reason: string | null | undefined;
  scopeKeys: string[];
  onClose: () => void;
}) {
  return (
    <>
      <DialogBody>
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/40">
          <Lock className="mt-0.5 size-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="space-y-1 text-sm text-amber-900 dark:text-amber-200">
            <p>{reason}</p>
            {scopeKeys.length > 0 && (
              <p className="text-xs">
                It currently has {scopeKeys.length} permission
                {scopeKeys.length === 1 ? "" : "s"}.
              </p>
            )}
          </div>
        </div>
      </DialogBody>
      <DialogFooter className="px-6 pb-6">
        <Button onClick={onClose}>Got it</Button>
      </DialogFooter>
    </>
  );
}

function AmendFields({
  request,
  baseScopeKeys,
  lockedScopeKeys,
  signInRegistration,
  onClose,
}: {
  request: AppRegistrationRequestDetail;
  /** What the registration holds now, as the server answered it. */
  baseScopeKeys: string[];
  /** What the server always keeps on this registration (`lockedScopeKeysOf`). */
  lockedScopeKeys: readonly string[];
  /** The project's own sign-in registration: sign-in alone is a valid result. */
  signInRegistration: boolean;
  onClose: () => void;
}) {
  const amend = useCreateAppRegistrationAmendment(request.id, request.project_id);
  const { data: scopes } = useAppRegistrationScopes();
  // Seeded from the server's answer, which is also what gets echoed back as
  // `base_scope_keys`. One value, one source — that is what makes the
  // compare-and-swap compare the same question the requester answered.
  // The locked scopes are held out of the editable selection: the server
  // re-adds them to every amendment, so offering their removal would show a
  // diff that never happens.
  const [selected, setSelected] = useState<string[]>(() =>
    withoutLockedScopes(baseScopeKeys, lockedScopeKeys),
  );
  const [purpose, setPurpose] = useState("");
  const [error, setError] = useState("");

  // What the server will store. A registration that predates injection shows
  // the locked scopes as added, which is exactly what approving it does.
  const requested = useMemo(
    () => withLockedScopes(selected, lockedScopeKeys),
    [selected, lockedScopeKeys],
  );
  // "openid, profile, email" (and "User.Read" on a sign-in registration), by
  // the names the picker uses; the bare key until the catalogue arrives.
  const lockedLabels = useMemo(() => {
    const catalogue = indexScopes(scopes);
    return lockedScopeKeys
      .map((key) => catalogue.get(key)?.value ?? key.replace(/^graph:/, ""))
      .join(", ");
  }, [scopes, lockedScopeKeys]);
  // The server caps permissions beyond openid/profile/email at 25, so a locked
  // scope outside those (User.Read on a sign-in registration) uses one slot.
  const maxScopes =
    MAX_SCOPES - lockedScopeKeys.filter((key) => !IDENTITY_SCOPE_KEYS.includes(key)).length;
  const diff = useMemo(() => diffScopeKeys(baseScopeKeys, requested), [baseScopeKeys, requested]);
  const unchanged = diff.added.length === 0 && diff.removed.length === 0;
  const submitting = amend.isPending;

  const toggleScope = (key: string) => {
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const validate = (): string => {
    if (unchanged) {
      return "Nothing has changed yet — add or remove a permission first.";
    }
    // A desk registration exists to call something beyond sign-in; the
    // project's sign-in registration exists to sign people in, so it may be
    // amended back down to just that.
    if (!signInRegistration && withoutLockedScopes(selected, lockedScopeKeys).length === 0) {
      return "Keep at least one permission beyond sign-in.";
    }
    if (purpose.trim().length < MIN_PURPOSE_LENGTH) {
      return `Say why the permissions are changing — at least ${MIN_PURPOSE_LENGTH} characters.`;
    }
    if (selected.length > maxScopes) {
      return `Select at most ${maxScopes} permissions.`;
    }
    return "";
  };

  const handleSubmit = async () => {
    if (submitting) {
      return;
    }
    setError("");
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await amend.mutateAsync({
        purpose: purpose.trim(),
        scope_keys: selected,
        base_scope_keys: baseScopeKeys,
      });
      onClose();
    } catch (err) {
      logger.error("[app-registration] amendment failed:", err);
      setError(
        readApiErrorMessage(err, "Failed to submit the change. Reopen the dialog and try again."),
      );
    }
  };

  return (
    <>
      <DialogBody>
        <div className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-baseline justify-between gap-2">
              <Label htmlFor="app-reg-scopes">Delegated permissions</Label>
              <span className="text-muted-foreground text-xs">
                {selected.length} of {maxScopes} selected
              </span>
            </div>
            <p className="text-muted-foreground text-xs">
              Starting from what the registration has now. Every permission acts as the signed-in
              user and can never reach data that user cannot already reach. Sign-in ({lockedLabels})
              is always kept
              {signInRegistration
                ? " — this is the project's sign-in registration, so nobody could sign in without it."
                : "."}
            </p>
            {/* Sign-in is always kept, so it is not offered: picking it would
                change nothing but the counter, and could use up the limit. */}
            <ScopePicker
              selected={selected}
              onToggle={toggleScope}
              disabled={submitting}
              atLimit={selected.length >= maxScopes}
              excluded={lockedScopeKeys}
            />
          </div>

          <div className="space-y-2">
            <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
              What would change
            </p>
            <PermissionDiff base={baseScopeKeys} requested={requested} />
          </div>

          {/* Shown while composing, not after submitting. A requester who only
              learns the removal is not a revocation once the change is live
              has already told somebody it was. */}
          <ConsentConsequences
            added={diff.added}
            removed={diff.removed}
            consentUrl={request.admin_consent_url}
            signInRegistration={signInRegistration}
          />

          <div className="space-y-1.5">
            <Label htmlFor="app-reg-amend-purpose">Why is this changing?</Label>
            <Textarea
              id="app-reg-amend-purpose"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="e.g. The capacity report now also summarises the user's unread mail, so it needs Mail.Read."
              disabled={submitting}
              rows={3}
            />
            <p className="text-muted-foreground text-xs">
              The approver reads this. The registration&apos;s original purpose is already on the
              request — say what is different.
            </p>
          </div>

          {error && (
            <p className="text-destructive flex items-start gap-2 text-sm">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              <span>{error}</span>
            </p>
          )}
        </div>
      </DialogBody>

      <DialogFooter className="px-6 pb-6">
        <Button variant="outline" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={submitting || unchanged}>
          {submitting ? "Submitting…" : "Request this change"}
        </Button>
      </DialogFooter>
    </>
  );
}
